import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useSurfaceColor } from '..';
import CircularProgressIndicator from '../CircularProgressIndicator';
import { useTheme } from '@emotion/react';
import { IconContext } from '@talismn/web-icons/utils';
import { useCallback, useMemo, useTransition } from 'react';
const Button = ({ as = 'button', variant, trailingIcon, leadingIcon, hidden, loading: _loading = false, withTransition = false, ...props }) => {
    const theme = useTheme();
    const [isPending, startTransition] = useTransition();
    const loading = _loading || isPending;
    const disabled = Boolean(props.disabled) || Boolean(hidden) || Boolean(loading);
    const surfaceColor = useSurfaceColor();
    const variantStyle = useMemo(() => {
        switch (variant) {
            case 'secondary':
            case 'surface':
                return {
                    backgroundColor: surfaceColor,
                    color: theme.color.onSurface,
                    ':hover': {
                        opacity: 0.6,
                    },
                };
            case 'outlined':
                return {
                    backgroundColor: 'transparent',
                    color: theme.color.onBackground,
                    borderStyle: 'solid',
                    borderColor: theme.color.onBackground,
                    borderWidth: 1,
                    ':hover': {
                        color: theme.color.background,
                        backgroundColor: theme.color.onBackground,
                    },
                };
            case 'tonal':
                return {
                    backgroundColor: `color-mix(in srgb, ${theme.color.primary}, transparent 90%)`,
                    color: theme.color.primary,
                    ':hover': {
                        opacity: 0.6,
                    },
                };
            case 'text':
                return {
                    backgroundColor: 'transparent',
                    color: theme.color.primary,
                };
            case 'noop':
                return {
                    padding: 'none',
                    margin: 'none',
                    background: 'none',
                    border: 'none',
                    outline: 'none',
                    ':hover': {
                        filter: 'brightness(1.5)',
                    },
                };
            case undefined:
                return {
                    backgroundColor: theme.color.primaryContainer,
                    color: theme.color.onPrimary,
                    ':hover': {
                        opacity: 0.6,
                    },
                };
        }
    }, [
        surfaceColor,
        theme.color.background,
        theme.color.onBackground,
        theme.color.onPrimary,
        theme.color.onSurface,
        theme.color.primary,
        theme.color.primaryContainer,
        variant,
    ]);
    const variantDisabledStyle = useMemo(() => {
        switch (variant) {
            case undefined:
                return {
                    backgroundColor: surfaceColor,
                    color: `color-mix(in srgb, ${theme.color.onSurface}, transparent ${Math.round((1 - theme.contentAlpha.disabled) * 100)}%)`,
                };
            default:
                return { filter: 'grayscale(1) brightness(0.5)' };
        }
    }, [surfaceColor, theme.color.onSurface, theme.contentAlpha.disabled, variant]);
    const hasLeadingIcon = Boolean(loading) || leadingIcon !== undefined;
    const onClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (...args) => {
        if (withTransition) {
            return startTransition(() => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ;
                props.onClick?.(...args);
            });
        }
        else {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            return props.onClick?.(...args);
        }
    }, [props, withTransition]);
    const Component = as;
    return (_jsx(Component
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    , { ...props, onClick: onClick, disabled: disabled, css: [
            theme.typography.body,
            {
                textAlign: 'center',
                display: 'inline-flex',
                padding: '1.156rem 2.4rem',
                width: 'fit-content',
                border: 'none',
                borderRadius: theme.shape.full,
                cursor: 'pointer',
                transition: '.25s',
                ...variantStyle,
                ...(disabled ? { ':hover': undefined } : {}),
            },
            hasLeadingIcon && { paddingInlineStart: '1.6rem' },
            trailingIcon && { paddingInlineEnd: '1.6rem' },
            loading && { cursor: 'wait' },
            props.disabled && [{ cursor: 'not-allowed' }, variantDisabledStyle],
            hidden && { cursor: 'default', pointerEvent: 'none', opacity: 0 },
        ], children: _jsxs("div", { css: { flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.8rem' }, children: [hasLeadingIcon && (_jsx(IconContext.Provider, { value: { size: '1.8rem' }, children: (() => {
                        if (loading) {
                            return _jsx(CircularProgressIndicator, { size: "1.6rem" });
                        }
                        if (leadingIcon) {
                            return _jsx("div", { css: { display: 'flex' }, children: leadingIcon });
                        }
                        return undefined;
                    })() })), _jsx("span", { css: { textAlign: 'center' }, children: props.children }), trailingIcon && (_jsx(IconContext.Provider, { value: { size: '1.8rem' }, children: _jsx("div", { css: { display: 'flex' }, children: trailingIcon }) }))] }) }));
};
export default Button;
export const OutlinedButton = (props) => (
// @ts-expect-error
_jsx(Button, { ...props, variant: "outlined" }));
export const TonalButton = (props) => (
// @ts-expect-error
_jsx(Button, { ...props, variant: "tonal" }));
export const TextButton = (props) => (
// @ts-expect-error
_jsx(Button, { ...props, variant: "text" }));
export const SurfaceButton = (props) => (
// @ts-expect-error
_jsx(Button, { ...props, variant: "surface" }));
//# sourceMappingURL=Button.js.map